import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { NavigationEnd, Router } from '@angular/router'
import { RafflesFacade } from '@galabid/gb-dashboard/raffle/data-access'
import { SharedUiModule } from '@galabid/shared/ui'
import { GbColorService } from '@galabid/shared/utils/services'
import { TranslateModule } from '@ngx-translate/core'
import { MOCK_RAFFLE_WINNERS } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.mocks'
import {
  RaffleWinner,
  RaffleWinnersWidget,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { GbRaffleDrawStore } from 'libs/gb-dashboard/raffle/data-access/src/lib/+state/gb-raffle-draw.store'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { combineLatest, concat, interval, of } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { GbPresenterWidgetRaffleItemLabelComponent } from '../gb-presenter-widget-raffle-item-label/gb-presenter-widget-raffle-item-label.component'
import { PresenterConstants } from '../presenter.constants'

@Component({
  selector: 'gb-presenter-widget-raffle-winners-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    GbPresenterWidgetPlaceholderComponent,
    GbPresenterWidgetRaffleItemLabelComponent,
    MatProgressSpinnerModule,
    GbDashboardSharedUiModule,
    SharedUiModule,
  ],
  providers: [GbRaffleDrawStore],
  templateUrl: './gb-presenter-widget-raffle-winners-list.component.html',
  styleUrl: './gb-presenter-widget-raffle-winners-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetRaffleWinnersListComponent {
  private rafflesFacade = inject(RafflesFacade)
  private raffleDrawStore = inject(GbRaffleDrawStore)
  private gbColorService = inject(GbColorService)
  private router = inject(Router)
  private colorResolver = inject(GbPresenterColorResolverService)

  @Output() openRaffleItemWarning = new EventEmitter<void>()

  slideIntervalSeconds = 5

  isTemplateMode = input(false)

  testData = input(false)

  inPreviewMode = input(false)

  raffleWinnersWidget = input.required<RaffleWinnersWidget>()

  exampleData = computed(() => {
    const count = this.raffleWinnersWidget().prizes_available ?? 0
    let winnersCount = count
    if (this.isTemplateMode()) {
      winnersCount = 5
    }
    return MOCK_RAFFLE_WINNERS.slice(0, Math.max(0, winnersCount))
  })

  private allRaffleItems = toSignal(this.rafflesFacade.allRaffleItems$, { initialValue: [] })
  private raffleDraws = toSignal(this.raffleDrawStore.raffleDraws$, { initialValue: [] })
  private rafflesFacadeLoaded = toSignal(this.rafflesFacade.loaded$, { initialValue: false })
  private raffleDrawsLoading = toSignal(this.raffleDrawStore.loading$, { initialValue: false })

  loaded = computed(() => {
    if (this.isTemplateMode()) {
      return true
    }
    return this.rafflesFacadeLoaded() && !this.raffleDrawsLoading()
  })

  filteredRaffleDraws = computed(() =>
    this.raffleDraws().filter((draw) => draw.raffle_item === this.raffleWinnersWidget().raffle_item),
  )

  isLaunchMode$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.router.url.includes(`/${PresenterConstants.VIEW}`)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  isLaunchMode = toSignal(this.isLaunchMode$, { initialValue: false })

  displayRaffleDraws = computed(() => this.filteredRaffleDraws().length > 0)

  raffleItemName = computed(() => {
    const widget = this.raffleWinnersWidget()
    return this.allRaffleItems().find((item) => item.raffle_item?.uuid === widget.raffle_item)?.name
  })

  backgroundColor = computed(() => {
    const widget = this.raffleWinnersWidget()
    return (
      this.colorResolver.resolve(widget?.background_color_override) ||
      this.colorResolver.resolve(widget?.background_color) ||
      'transparent'
    )
  })

  textColor = computed(() => {
    const widget = this.raffleWinnersWidget()
    return this.colorResolver.resolve(widget?.font_color) || this.colorResolver.resolvedGlobalColor() || '#000000'
  })

  leftBackgroundColor = computed(
    () => this.colorResolver.resolve(this.raffleWinnersWidget()?.accent_color) || 'transparent',
  )

  rightBackgroundColor = computed(
    () => this.colorResolver.resolve(this.raffleWinnersWidget()?.primary_color) || 'transparent',
  )

  leftTextColor = computed(() => this.gbColorService.getContrast(this.leftBackgroundColor()))

  rightTextColor = computed(() => this.gbColorService.getContrast(this.rightBackgroundColor()))

  fontSizeCqw = computed(() => {
    const px = this.raffleWinnersWidget()?.font_size ?? 16
    return px / 8
  })

  fontFamily = computed(
    () => this.raffleWinnersWidget()?.font_family || this.colorResolver.globalFontFamily() || 'Arial, sans-serif',
  )

  fontWeight = computed(() => this.raffleWinnersWidget()?.font_weight || 'normal')

  textAlign = computed(() => this.raffleWinnersWidget()?.text_align || 'left')

  justifyContent = computed(() => {
    switch (this.textAlign()) {
      case 'center':
        return 'center'
      case 'right':
        return 'flex-end'
      default:
        return 'flex-start'
    }
  })

  winners = computed<RaffleWinner[]>(() => {
    if (!this.testData()) {
      return this.filteredRaffleDraws().map((draw) => ({
        ...draw,
        winner_name: draw.profile_name,
        prize_name: draw.prize.toString(),
        ticket_number: draw.ticket_number.toString(),
      }))
    } else {
      return this.exampleData()
    }
  })

  previewIndex = signal(0)

  previewMode = computed(() => this.previewIndex() > 0)

  pageSize = computed(() => {
    const widget = this.raffleWinnersWidget()
    return Math.max(0, (widget.rows || 0) * (widget.columns || 0))
  })

  totalPages = computed(() => {
    const pageSize = this.pageSize()
    const winners = this.winners()
    if (!pageSize || !winners?.length) return 1
    return Math.max(1, Math.ceil(winners.length / pageSize))
  })

  prevPageIndex = 0

  pageIndex$ = combineLatest([
    toObservable(this.totalPages),
    toObservable(this.winners),
    this.raffleDrawStore.realtimeDraw$.pipe(startWith(null)),
    toObservable(this.previewMode),
  ]).pipe(
    switchMap(([totalPages, winners, realtimeDraw, isPreviewMode]) => {
      const safeStartPage = 0
      let page = this.prevPageIndex && this.prevPageIndex < totalPages ? this.prevPageIndex : safeStartPage

      if (realtimeDraw) {
        const newDrawIndex = winners.findIndex((winner) => winner.uuid === realtimeDraw.uuid)
        page = Math.floor(newDrawIndex / this.pageSize())
      }

      if (totalPages <= 1 || isPreviewMode) {
        return of(safeStartPage)
      }

      return concat(
        of(page),
        interval(this.slideIntervalSeconds * 1000).pipe(scan((prevPage) => (prevPage + 1) % totalPages, page)),
      )
    }),
    tap((page) => (this.prevPageIndex = page)),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pageIndex = toSignal(this.pageIndex$, { initialValue: 0 })

  displayWinners = computed(() => {
    const pageSize = this.pageSize()
    const pageIndex = this.pageIndex()
    const winners = this.winners()
    const testData = this.testData()
    const previewIndex = this.previewIndex()

    if (!pageSize) return []

    const isPreview = this.previewMode()
    const sourceWinners = testData || isPreview ? this.exampleData() : winners
    const safeWinners = sourceWinners || []

    const start = pageIndex * pageSize
    const pageSlice = safeWinners.slice(start, start + pageSize)

    if (isPreview) {
      const previewSlice = safeWinners.slice(0, previewIndex)

      if (pageSlice.length <= previewSlice.length) {
        return pageSlice
      }

      return previewSlice
    }

    return pageSlice
  })

  gridTemplateColumns = computed(() => {
    const columns = Math.max(1, this.raffleWinnersWidget()?.columns ?? 1)
    return `repeat(${columns}, 1fr)`
  })

  gridTemplateRows = computed(() => {
    const rows = Math.max(1, this.raffleWinnersWidget()?.rows ?? 1)
    return `repeat(${rows}, 1fr)`
  })

  private raffleItemUuid: string | null = null

  constructor() {
    effect(
      () => {
        const raffleItem = this.raffleWinnersWidget().raffle_item
        if (raffleItem && raffleItem !== this.raffleItemUuid) {
          this.raffleItemUuid = raffleItem
          this.raffleDrawStore.loadFirstRaffleDrawsPage({ raffleItemUuid: this.raffleItemUuid })
        }
      },
      { allowSignalWrites: true },
    )

    this.pageIndex$
      .pipe(
        withLatestFrom(this.raffleDrawStore.state$),
        tap(([pageIndex, storeState]) => {
          if (!this.raffleItemUuid) return
          const nextSlideEnd = (pageIndex + 1) * this.pageSize()
          if (nextSlideEnd >= storeState.results.length && storeState.results.length < storeState.count) {
            this.raffleDrawStore.loadNextRaffleDrawsPage({ raffleItemUuid: this.raffleItemUuid })
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  previewDraw(): void {
    setTimeout(() => {
      this.previewIndex.update((value) => {
        if (this.raffleWinnersWidget().prizes_available === value) {
          return value
        }
        return value + 1
      })
    }, 3000)
  }

  resetPreview() {
    this.previewIndex.set(0)
  }

  getLeftLabel(winner: RaffleWinner): string {
    if (this.raffleWinnersWidget()?.show_ticket_numbers === false) {
      return ''
    }

    return winner?.ticket_number || ''
  }

  getRightLabel(winner: RaffleWinner): string {
    const parts: string[] = []

    if (this.raffleWinnersWidget()?.show_prize_names !== false) {
      const prize = winner?.prize_name || ''
      if (prize) {
        parts.push(prize)
      }
    }

    const name = winner?.winner_name || ''
    if (name) {
      parts.push(name)
    }

    return parts.join(' - ')
  }
}
